import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  Modal,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ContentCopy,
  EditNote,
  LinkOutlined,
  MailOutline,
  SaveOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import Controls from "../../../atoms/Controls";
import FlatCard from "../../../atoms/FlatCard";
import { observer } from "mobx-react";
import useStore from "../../../helpers/useStore";
import { useMasterTables } from "../../../stores/MasterTables";
import { useLocations, useAllUsersBasic } from "../../../stores/User/user.queries";
import { useSaveUserInfo } from "../../../stores/User/user.mutations";
import { UserContext } from "../../../stores/User/User.provider";
import { TherapyType, UserDto } from "../../../api-client";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useUnsavedChanges } from "./UnsavedChangesGuard";

const BUNDESLAND_OPTIONS = [
  { code: "Baden-Württemberg", name: "Baden-Württemberg" },
  { code: "Bayern", name: "Bayern" },
  { code: "Berlin", name: "Berlin" },
  { code: "Brandenburg", name: "Brandenburg" },
  { code: "Bremen", name: "Bremen" },
  { code: "Hamburg", name: "Hamburg" },
  { code: "Hessen", name: "Hessen" },
  { code: "Mecklenburg-Vorpommern", name: "Mecklenburg-Vorpommern" },
  { code: "Niedersachsen", name: "Niedersachsen" },
  { code: "Nordrhein-Westfalen", name: "Nordrhein-Westfalen" },
  { code: "Rheinland-Pfalz", name: "Rheinland-Pfalz" },
  { code: "Saarland", name: "Saarland" },
  { code: "Sachsen", name: "Sachsen" },
  { code: "Sachsen-Anhalt", name: "Sachsen-Anhalt" },
  { code: "Schleswig-Holstein", name: "Schleswig-Holstein" },
  { code: "Thüringen", name: "Thüringen" },
];

/**
 * How many permission chips a role card shows before collapsing the rest into
 * a "+N" chip. Roles carry up to a dozen permissions each; spelling them all
 * out is what used to push the save button below the fold. The full list is
 * one tab over ("Berechtigung") and in the overflow chip's tooltip.
 */
const VISIBLE_PERMISSION_CHIPS = 4;

/** Project a user record onto the shape this form edits. */
const buildFormValues = (userData: any): UserDto => ({
  id: userData?.id,
  firstName: userData?.firstName || "",
  lastName: userData?.lastName || "",
  nickName: userData?.nickName || "",
  username: userData?.username || "",
  email: userData?.email || "",
  phone: userData?.phone || "",
  address: userData?.address || "",
  bundesland: userData?.bundesland || "",
  locationId: userData?.locationId || "",
  therapistLocationId: userData?.therapistLocationId || "",
  roles: userData?.roles?.map((role: any) => role.id) || [],
  // Only consulted by the backend at CREATION (default therapy grants); kept
  // in the form shape so the dirty check and save payload see a stable value.
  therapistDiscipline: userData?.therapistDiscipline || TherapyType.Physiotherapie,
  lastWorkDay: userData?.lastWorkDay
    ? new Date(userData.lastWorkDay).toISOString().split("T")[0]
    : undefined,
  vacationDays: userData?.vacationDays ?? null,
});

/** Role selection is a set, not a list — order must not read as a change. */
const sameIdSet = (a?: any[], b?: any[]) =>
  JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

const BasicUserInfo = observer(({ userData, onSelectUser }: { userData: any; onSelectUser: any }) => {
  const { data: masterTablesData } = useMasterTables();
  const { data: locations } = useLocations();

  const [loading, setLoading] = useState(false);

  const UserStore = useStore(UserContext);
  const { sendActivationEmail, sendUserActivationEmail } = UserStore;
  const saveUserInfoMutation = useSaveUserInfo();
  const { data: allUsersBasic = [] } = useAllUsersBasic(); // Default withDeleted=false
  const { setUnsavedChanges } = useUnsavedChanges();

  const roles: any[] = (masterTablesData || [])
    .filter((obj: any) => "role" in obj)
    .flatMap((obj: any) => obj.role ?? []);

  // `handleSave` resolves the role ids in `formState` against this catalogue and
  // PUTs the result as the user's complete role list. An empty catalogue is NOT
  // the same as "this user has no roles": while master tables are in flight (or
  // if that query errored) every id resolves to nothing, and saving would post
  // `roles: []` and strip the user of every role — with the Rollen card showing
  // no checkboxes, so nothing on screen hints that it is about to happen.
  // Gate the save on the catalogue instead. Same shape as `isRecordLoaded` in
  // Therapies.tsx, which guards the therapy grants against the identical hazard.
  const isRoleCatalogLoaded = roles.length > 0;

  const initialFormValues: UserDto = buildFormValues(userData);
  const userId: string | undefined = userData?.id;

  const [formState, setFormState] = useState<UserDto>(initialFormValues);
  const [activationLink, setActivationLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const isEditing = Boolean(userId);

  // The activation link is always available for a saved user, regardless of
  // whether they have an email. The "send email" button only appears when there
  // is an email address to send to. Both are derived from formState rather than
  // held in state, so navigating between users can never leave stale visibility
  // behind (e.g. offering "E-Mail Aktivierung" for a user without an email).
  const showActivationButton = !!formState.id;
  const showEmailActivationButton = !!formState.id && !!formState.email?.trim();

  // Re-sync when a *different* user is loaded into this view. Keyed on the id
  // rather than the `userData` object: the parent rebuilds that object on data
  // refreshes, and re-syncing on identity alone would silently discard the
  // admin's unsaved edits.
  const previousUserIdRef = useRef<string | undefined>(userId);
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // A draft picking up its id on first save is the same editing session, not
    // a different user — leave the state handleSave just set alone.
    if (previousUserId === userId || !previousUserId) return;

    setFormState(buildFormValues(userData));
    // Activation state belongs to the user it was produced for. Never let a
    // generated reset link outlive that user — it carries a 2-day auth token,
    // so leaving it on screen under a different user's name would hand the
    // admin a token for the wrong account.
    setActivationLink(null);
    setCopySuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleInputChange = (fieldName: string, value: any) => {
    setFormState((prevFormState) => {
      const next = { ...prevFormState, [fieldName]: value };
      // When creating a new user, default the initial selected location
      // (locationId) to the chosen primary location (therapistLocationId) so
      // the user's first login lands them on the right location. We only
      // auto-fill while the selected location is still empty so we never
      // overwrite an explicit choice made by the admin.
      if (!isEditing && fieldName === "therapistLocationId" && !prevFormState.locationId) {
        next.locationId = value;
      }
      return next;
    });
  };

  // Kept as a text field (the shared Input atom has no number mode); digits only
  // so a cleared field round-trips as null rather than NaN.
  const handleVacationDaysChange = (rawValue: string) => {
    const digitsOnly = rawValue.replace(/\D/g, "");
    handleInputChange("vacationDays", digitsOnly === "" ? null : Number(digitsOnly));
  };

  const handleCheckboxChange = (role: any) => {
    const { roles: selectedRoles } = formState;
    const updatedRoles = selectedRoles
      ? selectedRoles.includes(role.id)
        ? selectedRoles.filter((selectedRole: any) => selectedRole !== role.id)
        : [...selectedRoles, role.id]
      : [role.id];

    setFormState((prevFormState) => ({
      ...prevFormState,
      roles: updatedRoles,
    }));
  };

  /** Resolves to `false` when the save was rejected and the form still holds edits. */
  const handleSave = async (): Promise<boolean> => {
    try {
      setLoading(true);

      // For new users, the primary work location (Einsatzort) is mandatory.
      // The backend also enforces this, but we short-circuit here for a
      // friendlier UX rather than waiting for the server round-trip.
      if (!isEditing && !formState.therapistLocationId) {
        notificationStore.showMessage(
          "Bitte einen Einsatzort (primärer Standort) auswählen, bevor der Benutzer angelegt wird.",
          "warning"
        );
        return false;
      }

      // Same belt-and-braces reasoning as the location check: `saveDisabled`
      // already blocks the button, but the unsaved-changes guard can call
      // handleSave on navigate-away without going near it.
      if (!formState.bundesland) {
        notificationStore.showMessage(
          "Bitte ein Bundesland auswählen – das Feld ist ein Pflichtfeld.",
          "warning"
        );
        return false;
      }

      // Check if any user already has the same email
      const existingUserWithUserId = allUsersBasic.find(
        (user) =>
          formState.email &&
          formState.email.trim() !== "" &&
          user.email?.toLowerCase() === formState.email?.toLowerCase() &&
          user.id !== formState.id
      );
      // Check if any user already has the same nickName
      const existingUserWithNickName = allUsersBasic.find(
        (user) =>
          formState.nickName &&
          user.nickName?.toLowerCase() === formState.nickName?.toLowerCase() &&
          user.id !== formState.id
      );
      // Check if any user already has the same username
      const existingUserWithUsername = allUsersBasic.find(
        (user) =>
          formState.username &&
          formState.username.trim() !== "" &&
          user.username?.toLowerCase() === formState.username?.toLowerCase() &&
          user.id !== formState.id
      );

      if (existingUserWithUsername) {
        notificationStore.showMessage("Benutzername existiert bereits", "warning");
        return false;
      }

      if (existingUserWithNickName) {
        notificationStore.showMessage("Kürzel bereits vorhanden", "warning");
        return false;
      }

      if (existingUserWithUserId) {
        notificationStore.showMessage("Benutzer-ID/Mail existiert bereits", "warning");
        return false;
      }

      const locationData = locations?.find(
        (location: any) => location.id.toString() === formState.locationId
      );
      const therapistLocationData = locations?.find(
        (location: any) => location.id.toString() === formState.therapistLocationId
      );
      // Belt and braces alongside `saveDisabled`: the unsaved-changes guard can
      // invoke handleSave on navigate-away without going through the button, so
      // this path has to defend itself. `undefined` (not `[]`) is what tells the
      // backend to leave the existing roles alone.
      if (!isRoleCatalogLoaded) {
        notificationStore.showMessage(
          "Rollen konnten noch nicht geladen werden – bitte erneut versuchen.",
          "warning"
        );
        return false;
      }

      const rolesData = roles.filter((role: any) => formState.roles?.includes(role.id));

      // Create a complete user object with location and roles details
      const completeUserObject = {
        ...formState,
        location: locationData,
        therapistLocation: therapistLocationData,
        roles: rolesData,
      };

      const response = await saveUserInfoMutation.mutateAsync(completeUserObject);
      // Normalised through the same projection the dirty check compares
      // against — otherwise a field the backend returns as `null` where the
      // form holds `""` would leave the panel reading "nicht gespeichert"
      // immediately after a successful save.
      setFormState(buildFormValues(response));
      onSelectUser(response);
      return true;
    } catch (error) {
      console.error("Error saving user data:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => setFormState(buildFormValues(userData));

  const handleGenerateActivationLink = async () => {
    if (!formState.id) {
      notificationStore.showMessage("Kein Benutzer ausgewählt", "warning");
      return;
    }

    setLoading(true);
    try {
      const response = (await sendActivationEmail(formState.id)) as { link: string } | void;
      if (response?.link) {
        setActivationLink(response.link);
      }

      notificationStore.showMessage("Aktivierungslink wurde gesendet.", "success");
    } catch (error) {
      console.error("Error sending activation email:", error);
      notificationStore.showMessage("Fehler beim Senden des Aktivierungslinks", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendActivationEmail = async () => {
    if (!formState.id) {
      notificationStore.showMessage("Kein Benutzer ausgewählt", "warning");
      return;
    }

    setLoading(true);
    try {
      await sendUserActivationEmail(formState.id);
      notificationStore.showMessage("Aktivierungsmail wurde erfolgreich gesendet", "success");
    } catch (error) {
      console.error("Error sending activation email:", error);
      notificationStore.showMessage("Fehler beim Senden der Aktivierungsmail", "error");
    } finally {
      setLoading(false);
    }
  };

  const isDirty = !(
    formState.firstName === initialFormValues.firstName &&
    formState.lastName === initialFormValues.lastName &&
    formState.nickName === initialFormValues.nickName &&
    formState.username === initialFormValues.username &&
    formState.email === initialFormValues.email &&
    formState.phone === initialFormValues.phone &&
    formState.address === initialFormValues.address &&
    formState.bundesland === initialFormValues.bundesland &&
    formState.locationId === initialFormValues.locationId &&
    formState.therapistLocationId === initialFormValues.therapistLocationId &&
    sameIdSet(formState.roles, initialFormValues.roles) &&
    formState.therapistDiscipline === initialFormValues.therapistDiscipline &&
    formState.lastWorkDay?.toString().slice(0, 10) ===
      initialFormValues.lastWorkDay?.toString().slice(0, 10) &&
    (formState.vacationDays ?? null) === (initialFormValues.vacationDays ?? null)
  );

  // Publish the dirty state so closing the detail view, switching sub-tabs or
  // reloading the browser can warn instead of silently dropping the edits.
  // The save handler goes through a ref so the guard always invokes the
  // current closure rather than the one captured when `isDirty` last flipped.
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  useEffect(() => {
    setUnsavedChanges(isDirty, () => saveRef.current());
    return () => setUnsavedChanges(false);
  }, [isDirty, setUnsavedChanges]);

  const handleCopyLink = () => {
    if (!activationLink) return;

    navigator.clipboard.writeText(activationLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const missingRequiredLocation = !isEditing && !formState.therapistLocationId;
  // Unlike the location rule above, this one applies when editing too. MD-382
  // was reported precisely because clearing Bundesland on an existing user
  // looked like it saved and silently did nothing — a user record without a
  // Bundesland is incomplete whichever way it got that way, so the form says so
  // instead of accepting the edit and dropping it.
  const missingRequiredBundesland = !formState.bundesland;
  const saveDisabled =
    loading ||
    !isDirty ||
    missingRequiredLocation ||
    missingRequiredBundesland ||
    !isRoleCatalogLoaded;

  const locationSelectOptions = useMemo(
    () =>
      locations?.map((option: any) => ({
        label: option.name,
        value: option.name.toString(),
        id: option.id.toString(),
      })),
    [locations]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/*
        Action bar, pinned to the top of the scroll container. The save button
        used to sit below the role cards and fell past the fold, so it read as
        optional — changes look applied the moment a field is edited. Keeping
        it in view, next to an explicit dirty marker, is what makes "nothing is
        stored until you press this" legible.
      */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "var(--ink-line)",
          // Rule 4: state tints the edge, never the whole surface.
          borderLeft: "4px solid",
          borderLeftColor: isDirty ? "secondary.main" : "transparent",
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--elev-sticky-y)",
          transition: "border-color var(--motion-fast) ease-in-out",
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>Kontaktdaten &amp; Rollen</Typography>

        {isDirty ? (
          <Chip
            size="small"
            color="secondary"
            variant="outlined"
            icon={<EditNote sx={{ fontSize: 16 }} />}
            label="Nicht gespeicherte Änderungen"
            sx={{ fontWeight: 600 }}
          />
        ) : isEditing ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
            label="Alle Änderungen gespeichert"
            sx={{ color: "text.secondary" }}
          />
        ) : (
          <Chip size="small" variant="outlined" label="Neuer Benutzer – noch nicht angelegt" />
        )}

        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          <Button variant="text" onClick={handleDiscard} disabled={!isDirty || loading}>
            Verwerfen
          </Button>
          <Tooltip
            title={
              missingRequiredLocation
                ? "Einsatzort (Standort) ist für neue Benutzer ein Pflichtfeld"
                : missingRequiredBundesland
                  ? "Bundesland ist ein Pflichtfeld"
                  : !isRoleCatalogLoaded
                    ? "Rollen werden noch geladen – Speichern ist so lange deaktiviert"
                    : ""
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveOutlined />}
                onClick={handleSave}
                disabled={saveDisabled}
              >
                Speichern
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Grid container spacing={1.5} alignItems="stretch">
        {/* Left column: the record itself, plus the account-access actions,
            which balances the taller role grid on the right. */}
        <Grid item xs={12} md={5}>
          <Stack spacing={1.5} sx={{ height: "100%" }}>
            <FlatCard title="Kontaktdaten" sx={{ flex: 1 }}>
              <Grid container columnSpacing={2} rowSpacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    label="Vorname"
                    value={formState.firstName}
                    onChange={(e: any) => handleInputChange("firstName", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    label="Nachname"
                    value={formState.lastName}
                    onChange={(e: any) => handleInputChange("lastName", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    label="Kürzel"
                    value={formState.nickName}
                    onChange={(e: any) => handleInputChange("nickName", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    label="Username"
                    value={formState.username}
                    onChange={(e: any) => handleInputChange("username", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Controls.Input
                    label="Mail"
                    value={formState.email}
                    onChange={(e: any) => handleInputChange("email", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Controls.Input
                    label="Handy"
                    value={formState.phone}
                    onChange={(e: any) => handleInputChange("phone", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Input
                    label="Adresse"
                    value={formState.address}
                    onChange={(e: any) => handleInputChange("address", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/*
                    "Standort" is the user's primary work location. This is the
                    field admins actually care about — the user's home/base
                    location. Under the hood it persists as
                    `therapistLocationId` on the user row, but we deliberately
                    surface it under the unambiguous label "Standort"
                    everywhere in the admin UI.
                  */}
                  <Controls.Select
                    name="therapistLocationId"
                    label="Standort"
                    value={formState.therapistLocationId}
                    id={1}
                    required={!isEditing}
                    error={missingRequiredLocation ? "Pflichtfeld" : null}
                    onChange={(e: any) => handleInputChange("therapistLocationId", e.target.id)}
                    options={locationSelectOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Select
                    name="bundesland"
                    label="Bundesland"
                    value={formState.bundesland}
                    required
                    error={missingRequiredBundesland ? "Pflichtfeld" : null}
                    onChange={(e: any) => handleInputChange("bundesland", e.target.value)}
                    options={BUNDESLAND_OPTIONS.map((option) => ({
                      label: option.name,
                      value: option.code,
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controls.Input
                    label="Urlaubstage pro Jahr"
                    value={formState.vacationDays ?? ""}
                    onChange={(e: any) => handleVacationDaysChange(e.target.value)}
                    fullWidth
                  />
                </Grid>
                {isEditing && (
                  <Grid item xs={12} sm={6}>
                    {/*
                      "Anzeige-Standort" is the user's last-selected viewing
                      location (persisted as `locationId`). It controls which
                      location the user lands on when they next log in. We only
                      expose it in edit mode for edge cases where it has drifted
                      from the primary location and an admin needs to correct
                      it. On user creation it is auto-set to match the primary
                      "Standort" (see `handleInputChange`), so we don't render
                      it at all there.
                    */}
                    <Controls.Select
                      name="locationId"
                      label="Anzeige-Standort"
                      value={formState.locationId}
                      onChange={(e: any) => handleInputChange("locationId", e.target.id)}
                      id={1}
                      options={locationSelectOptions}
                    />
                  </Grid>
                )}
                {isEditing && (
                  <Grid item xs={12} sm={6}>
                    <Controls.DatePicker
                      label="Letzter Arbeitstag"
                      value={
                        formState.lastWorkDay ? formState.lastWorkDay.toString().split("T")[0] : ""
                      }
                      onChange={(e: any) => handleInputChange("lastWorkDay", e?.target?.value || null)}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </FlatCard>

            {/*
              Account access is a set of one-shot side effects on the *saved*
              record — nothing here is part of the form, so it gets its own
              card and outlined (secondary) buttons that cannot be mistaken for
              the contained "Speichern" in the bar above.
            */}
            <FlatCard title="Kontozugang">
              {isEditing ? (
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {showActivationButton && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<LinkOutlined />}
                        onClick={handleGenerateActivationLink}
                      >
                        Aktivierungslink generieren
                      </Button>
                    )}
                    {showEmailActivationButton && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<MailOutline />}
                        onClick={handleSendActivationEmail}
                      >
                        E-Mail Aktivierung
                      </Button>
                    )}
                  </Stack>

                  {activationLink && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Reset-Link (2 Tage gültig)
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Controls.Input
                            fullWidth
                            value={activationLink}
                            InputProps={{ readOnly: true }}
                          />
                        </Box>
                        <Button
                          onClick={handleCopyLink}
                          variant="outlined"
                          size="small"
                          startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                        >
                          Kopieren
                        </Button>
                      </Stack>
                      {copySuccess && (
                        <Typography variant="caption" color="primary">
                          Link kopiert!
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Aktivierung ist verfügbar, sobald der Benutzer angelegt wurde.
                </Typography>
              )}
            </FlatCard>
          </Stack>
        </Grid>

        {/* Right column: role selection. Deliberately kept here even though
            "Berechtigung" has its own tab — roles are part of setting a person
            up, the permission tab is for per-user exceptions. */}
        <Grid item xs={12} md={7}>
          <FlatCard
            title={
              <Box display="flex" alignItems="center" gap={0.75}>
                <VerifiedUserOutlined fontSize="small" sx={{ color: "secondary.main" }} />
                <span>Rollen</span>
              </Box>
            }
            fullHeight
          >
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Eine oder mehrere Rollen auswählen – jede Rolle bringt die angezeigten
                Berechtigungen mit.
              </Typography>
              <Grid container spacing={1}>
                {roles.map((role: any) => {
                  const isSelected = Boolean(formState.roles?.includes(role.id));
                  const permissions = role.permissions ?? [];
                  const shownPermissions = permissions.slice(0, VISIBLE_PERMISSION_CHIPS);
                  const hiddenPermissions = permissions.slice(VISIBLE_PERMISSION_CHIPS);
                  return (
                    <Grid item xs={12} sm={6} key={role.id}>
                      <Paper
                        elevation={0}
                        onClick={() => handleCheckboxChange(role)}
                        sx={{
                          height: "100%",
                          px: 1,
                          py: 0.75,
                          borderRadius: "var(--radius-card)",
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: isSelected ? "secondary.main" : "divider",
                          backgroundColor: isSelected
                            ? "rgba(156, 26, 53, 0.04)"
                            : "background.paper",
                          transition: "all var(--motion-fast) ease-in-out",
                          "&:hover": { borderColor: "secondary.main" },
                        }}
                      >
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(role)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0.25, mr: 0.75 }}
                          />
                          <Tooltip title={role.description || ""} arrow>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {role.name}
                            </Typography>
                          </Tooltip>
                        </Box>

                        {permissions.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {shownPermissions.map((permission: any) => (
                              <Tooltip
                                key={permission.id}
                                title={permission.description || ""}
                                arrow
                              >
                                <Chip
                                  label={permission.name}
                                  size="small"
                                  variant="outlined"
                                  color={isSelected ? "secondary" : "default"}
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </Tooltip>
                            ))}
                            {hiddenPermissions.length > 0 && (
                              <Tooltip
                                arrow
                                title={hiddenPermissions
                                  .map((permission: any) => permission.name)
                                  .join(", ")}
                              >
                                <Chip
                                  label={`+${hiddenPermissions.length}`}
                                  size="small"
                                  variant="outlined"
                                  color={isSelected ? "secondary" : "default"}
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontStyle: "italic" }}
                          >
                            Keine zusätzlichen Berechtigungen
                          </Typography>
                        )}

                        {/* Discipline of a NEW therapist — the backend uses it
                            once, at creation, to pick the default therapy
                            grants (Ergo: the whole ergo catalog; Physio: the
                            "Alle Physiotherapeuten" set). Existing users
                            manage grants on the Fortbildungen tab, so the
                            picker is hidden when editing. */}
                        {role.id === "therapist" && isSelected && !isEditing && (
                          <Box mt={1} onClick={(e) => e.stopPropagation()}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Fachrichtung – bestimmt die Standard-Heilmittel
                            </Typography>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              color="secondary"
                              value={formState.therapistDiscipline ?? TherapyType.Physiotherapie}
                              onChange={(_, value) => {
                                if (!value) return;
                                setFormState((prev) => ({
                                  ...prev,
                                  therapistDiscipline: value,
                                }));
                              }}
                            >
                              <ToggleButton value={TherapyType.Physiotherapie}>
                                Physiotherapie
                              </ToggleButton>
                              <ToggleButton value={TherapyType.Ergotherapie}>
                                Ergotherapie
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </FormControl>
          </FlatCard>
        </Grid>
      </Grid>

      <Modal
        open={loading}
        aria-labelledby="loading-modal-title"
        aria-describedby="loading-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>
          <CircularProgress size={60} />
        </div>
      </Modal>
    </Box>
  );
});

export default BasicUserInfo;
